import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { findTrack } from './tracks';

/**
 * Track reader — renders every chapter of a guide track as a single scrollable
 * page with a sticky table-of-contents that scroll-spies the active chapter.
 * Unknown or not-yet-written tracks bounce back to the hub.
 */
export function GuideTrackPage() {
  const { track: trackId } = useParams<{ track: string }>();
  const track = findTrack(trackId);
  const chapters = track?.chapters ?? [];
  const [activeId, setActiveId] = useState(chapters[0]?.id);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    if (!chapters.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the topmost section currently intersecting the upper band.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-72px 0px -65% 0px', threshold: 0 },
    );
    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackId]);

  if (!track || track.status !== 'live' || !chapters.length) {
    return <Navigate to="/guide" replace />;
  }

  const scrollTo = (id: string) => {
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveId(id);
  };

  const TrackIcon = track.icon;

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className={cn('absolute inset-x-0 top-0 h-28 bg-gradient-to-b opacity-70', track.accent)} aria-hidden />
        <div className="relative">
          <Link
            to="/guide"
            className="inline-flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All guides
          </Link>
          <div className="mt-3 flex items-center gap-3.5">
            <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-card text-primary ring-1 ring-inset ring-primary/20 shadow-sm">
              <TrackIcon className="h-7 w-7" />
            </span>
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wide text-primary">{track.tagline}</div>
              <h1 className="text-[24px] font-semibold tracking-tight text-foreground">{track.title}</h1>
            </div>
          </div>
          <p className="mt-3 max-w-2xl text-[13.5px] leading-relaxed text-muted-foreground">{track.description}</p>
          <div className="mt-3 flex items-center gap-4 text-[11.5px] text-muted-foreground">
            {track.minutes && (
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" /> {track.minutes} min read
              </span>
            )}
            <span>{chapters.length} chapters</span>
          </div>
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-[220px_1fr]">
        {/* Sticky TOC */}
        <aside className="hidden lg:block">
          <nav className="sticky top-20 space-y-0.5">
            <div className="mb-2 px-3 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              On this page
            </div>
            {chapters.map((c, i) => {
              const Icon = c.icon;
              const on = c.id === activeId;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => scrollTo(c.id)}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[12.5px] transition-colors',
                    on
                      ? 'bg-primary/8 font-medium text-foreground ring-1 ring-inset ring-primary/15'
                      : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
                  )}
                >
                  <Icon className={cn('h-4 w-4 shrink-0', on ? 'text-primary' : 'text-muted-foreground/70')} />
                  <span className="min-w-0 truncate">
                    <span className="tabular-nums text-muted-foreground/60">{i + 1}.</span> {c.title}
                  </span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Chapters */}
        <div className="min-w-0 space-y-5">
          {chapters.map((c, i) => {
            const Icon = c.icon;
            return (
              <section
                key={c.id}
                id={c.id}
                ref={(el) => (sectionRefs.current[c.id] = el)}
                className="scroll-mt-20 rounded-2xl border border-border bg-card p-6 shadow-sm"
              >
                <div className="mb-4 flex items-center gap-2.5 border-b border-border/60 pb-3">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary ring-1 ring-inset ring-primary/15">
                    <Icon className="h-[18px] w-[18px]" />
                  </span>
                  <div>
                    <div className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">
                      Chapter {i + 1}
                    </div>
                    <h2 className="text-[18px] font-semibold tracking-tight text-foreground">{c.title}</h2>
                  </div>
                </div>
                {c.render()}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
